import { memo, useMemo } from 'react'

import { BackButton } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { OpenSearchForm } from '~/features/common'
import { Office, Transportation } from '~/models/types'

import { useMapContext, useMapDispatchContext } from '../../context'
import { DrawerOfficeDetailCard } from '../drawer-office-detail-card'

const convertTransportationToText = (transportation: Transportation) => {
  switch (transportation) {
    case 'WALK':
      return '徒歩'
    case 'BUS':
      return 'バス'
    default:
      return ''
  }
}

const createTransportationInfo = (stations: Office['stations']) => {
  return stations
    .map((station) => {
      return `${station.name}駅${convertTransportationToText(station.transportation)}${station.time}分`
    })
    .join('、')
}

const createOtherAccessInfo = (otherAccesses: Office['otherAccesses']) => {
  if (!otherAccesses) return ''
  return otherAccesses
    .map((otherAccess) => {
      return `（${otherAccess.accessType === 'BUS' ? 'バス' : 'その他'}）${otherAccess.description}`
    })
    .join('、')
}

const DrawerSearchContentDetailComponent = () => {
  const { clickedPin } = useMapContext()
  const { onClickGoToList, openSearchForm } = useMapDispatchContext()
  const handleClick = () => {
    onClickGoToList()
  }

  const selectedOfficeForDetailCard: React.ComponentProps<
    typeof DrawerOfficeDetailCard
  > | null = useMemo(() => {
    const sortedImages = clickedPin?.images.sort(
      (a, b) => a.priority - b.priority,
    )
    const firstImage =
      sortedImages &&
      (sortedImages.find((image) => image.isEyeCatch) || sortedImages[0])?.url
    return clickedPin
      ? {
          address: clickedPin.address,
          facilityName: clickedPin.name,
          officeDetailLink: `/prefectures/${clickedPin.areas[0].prefectures[0].slug}/offices/${clickedPin.id}`,
          contactLink: `/prefectures/${clickedPin.areas[0].prefectures[0].slug}/offices/${clickedPin.id}/contact`,
          imageSrc: firstImage || NO_IMAGE_URL,
          transportationInfo: createTransportationInfo(clickedPin.stations),
          otherAccessInfo: createOtherAccessInfo(clickedPin.otherAccesses),
          buildingName: clickedPin.building || '',
          spaces: clickedPin.spaces
            .filter((space) => space.availability !== 'UNAVAILABLE')
            .map((space) => ({
              id: space.id,
              types: space.type.map((t) => t.name),
              price: space.monthlyCostExcludingTax,
              priceTaxIncluded: space.monthlyCostIncludingTax,
              pricePrivateFlag: space.isMonthlyCostPrivate,
              capacity: space.maxPeople,
            })),
        }
      : null
  }, [clickedPin])

  return (
    <div className="h-full w-full overflow-scroll bg-white pb-4 pt-2 shadow-lg">
      <div className="flex flex-row items-center justify-between px-4 pb-4">
        <BackButton onClick={handleClick}>一覧を表示</BackButton>
        <div className="w-[130px]">
          <OpenSearchForm onClick={openSearchForm}>条件検索</OpenSearchForm>
        </div>
      </div>
      {selectedOfficeForDetailCard && (
        <DrawerOfficeDetailCard
          address={selectedOfficeForDetailCard.address}
          buildingName={selectedOfficeForDetailCard.buildingName}
          contactLink={selectedOfficeForDetailCard.contactLink}
          facilityName={selectedOfficeForDetailCard.facilityName}
          imageSrc={selectedOfficeForDetailCard.imageSrc}
          officeDetailLink={selectedOfficeForDetailCard.officeDetailLink}
          otherAccessInfo={selectedOfficeForDetailCard.otherAccessInfo}
          spaces={selectedOfficeForDetailCard.spaces}
          transportationInfo={selectedOfficeForDetailCard.transportationInfo}
        />
      )}
    </div>
  )
}

export const DrawerSearchContentDetail = memo(
  DrawerSearchContentDetailComponent,
)
